import React from 'react'
import { Excel, Folder, Word, Powerpoint } from '@pergas-common/pergas-icons'
import SvgIcon from '@mui/material/SvgIcon'
import parseUrl from 'url-parse'

const fillC8 = { fill: '#c8c8c8' }
const fillRed = { fill: '#d65532' }

function PdfFileIcon (props) {
  return (
    <SvgIcon viewBox='0 0 14 16' style={{ height: 20, width: 20 }} {...props}>
      <g transform='translate(12379 16426)'>
        <path fill='#fff' d='M45 45H35V31h8l2 2z' transform='translate(-12412 -16456)' />
        <path fill='#a6a6a6' d='M43 30h-9v16h12V33zm0 1.5l1.5 1.5H43zM45 45H35V31h7v3h3z' transform='translate(-12412 -16456)' />
        <path d='M0 0H8V1H0z' style={fillC8} transform='translate(-12376 -16421)' />
        <path d='M0 0H8V1H0z' style={fillC8} transform='translate(-12376 -16419)' />
        <path d='M0 0H3V3H0z' style={fillRed} transform='translate(-12368 -16416)' />
        <path d='M0 0H3V3H0z' style={fillRed} transform='translate(-12379 -16416)' />
        <g transform='translate(-12412 -16456)'>
          <path d='M42 40v3h-4v-3h4m1-1h-6v5h6v-5z' style={fillRed} />
        </g>
        <path d='M0 0H3V1H0z' style={fillC8} transform='translate(-12376 -16424)' />
      </g>
    </SvgIcon>
  )
}

/**
 * Common file type extensions.
 */
export const EXCEL_FILES = ['xls', 'xlsx']
export const EXCEL_TEMPLATE_FILES = ['xlt', 'xltm', 'xltx']
export const POWERPOINT_FILES = ['ppt', 'pptx']
export const POWERPOINT_TEMPLATE_FILES = ['pot', 'potm', 'potx']
export const WORD_FILES = ['doc', 'docx']
export const WORD_TEMPLATE_FILES = ['dot', 'dotm', 'dotx']
export const TEMPLATE_FILES = EXCEL_TEMPLATE_FILES
  .concat(WORD_TEMPLATE_FILES)
  .concat(POWERPOINT_TEMPLATE_FILES)
const IMAGE_FILES = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'svg']
const EMAIL_FILES = ['msg', 'eml']

/**
 * Convert string to Uint8Array.
 */
export function stringToUint (string) {
  const array = new Array(string.length)
  const chars = string.split('')
  chars.forEach((c, i) => {
    array[i] = c.charCodeAt(0)
  })
  return Uint8Array.from(array)
}

/**
 * Cleans a file name from invalid characters.
 * Also tidies up multiples of spaces with a single space.
 */
export function cleanFileName (fileName) {
  let clean = fileName
    .replace(/[<>:'"/\\|?*#%]/g, '')
    .split(' ')
    .filter(Boolean)
    .join(' ')

  if (fileName.endsWith(' ')) {
    clean += ' '
  }

  return clean
}

/**
 * Adds https:// to an url if it's missing (for hrefs)
 */
export function httpify (url) {
  const lower = url.toLowerCase()
  if (lower.startsWith('http://') || lower.startsWith('https://')) {
    return url
  } else {
    return `https://${url}`
  }
}

/**
 * Compares two strings in lower case.
 */
function compareLowerCase (A, B) {
  const a = A.toLowerCase()
  const b = B.toLowerCase()
  if (a < b) return -1
  else if (a > b) return 1
  else return 0
}

/**
 * Sorts an array of folders and files.
 * Folders are sorted before files.
 * Folder and files are sorted between each other.
 */
export function sortFileItems (items) {
  return items.slice().sort((A, B) => {
    if (A.type === 'folder' && B.type === 'file') {
      return -1
    } else if (A.type === 'file' && B.type === 'folder') {
      return 1
    }
    return compareLowerCase(A.name, B.name)
  })
}

/**
 * Return true if a token has more than 90 seconds left
 */
export function isValidToken (token) {
  return typeof token === 'string' && getTokenExpiresInMs(token) > 90 * 1000
}

function getTokenExpiresInMs (token) {
  const { exp } = decodeToken(token)
  return exp * 1000 - Date.now()
}

/**
 * Decodes the payload from a json web token. Does not verify.
 */
export function decodeToken (token) {
  const parts = (token || '').split('.')

  if (parts.length !== 3) {
    return {}
  }

  let payload = {}

  try {
    payload = JSON.parse(atob(parts[1]))
  } catch (e) {
    console.error('Failed to parse encoded token payload')
  }

  return payload
}

/**
 * Return file extension from file name
 */
export function getFileExtension (name) {
  const split = name.split('.')
  return (split.length > 1 ? split[split.length - 1] : '').toLowerCase()
}

/**
 * Return true if file extension is an office zip file
 */
export function isOfficeZipFile (name) {
  return ['xlsx', 'docx', 'pptx'].includes(getFileExtension(name))
}

/**
 * Return office uri protocol based based on file extension.
 */
function getOfficeUriProtocol (ext) {
  if (WORD_FILES.includes(ext) ||
      WORD_TEMPLATE_FILES.includes(ext)) {
    return 'ms-word'
  } else if (POWERPOINT_FILES.includes(ext) ||
             POWERPOINT_TEMPLATE_FILES.includes(ext)) {
    return 'ms-powerpoint'
  } else if (EXCEL_FILES.includes(ext) ||
             EXCEL_TEMPLATE_FILES.includes(ext)) {
    return 'ms-excel'
  } else {
    return ''
  }
}

/**
 * Return true if extension is a template
 */
export function isTemplateFile (ext) {
  return TEMPLATE_FILES.includes(ext)
}

/**
 * Return an absolute url using host origin and a server relative url
 */
export function absoluteUrl (siteUrl, serverRelativeUrl) {
  const parsed = new URL(siteUrl)
  return encodeURI(`${parsed.origin}${serverRelativeUrl || ''}`)
}

/**
 * Converts keys and values from an object to a query string.
 */
export function toQueryString (params) {
  const query = Object.keys(params).map(key => {
    const value = params[key]
    if (typeof value === 'string') {
      return value ? `${key}=${encodeURI(value)}` : ''
    } else {
      return `${key}=${encodeURI(value)}`
    }
  }).filter(Boolean).join('&')
  return query ? `?${query}` : ''
}

export const constructPayload = (pdsFields, rest) => {
  // eslint-disable-next-line
  const {
    city,
    address,
    name,
    email,
    homepage,
    phone,
    collection_name: collectionName,
    object_type_name: objectTypeName,
    postal_code: postalCode
  } = pdsFields

  return {
    ...rest,
    pds_user: name ?? '',
    pds_email: email ?? '',
    pds_phone: phone,
    pds_city: city ?? '',
    pds_address: address ?? '',
    pds_userhomesite: homepage ?? '',
    pds_contact: collectionName ?? '',
    pds_title: objectTypeName ?? '',
    pds_zipcode: postalCode ?? ''
  }
}

/**
 * Open a document uri based on file type using the office uri scheme.
 * uri: full url to document or template
 * params: optional object which will passed as parameters to templates
 * **NOTE** Currently disabling query parameters for opening templates
 * on Mac!
 */
export function openDocument (uri, params) {
  const ext = getFileExtension(uri)
  const protocol = getOfficeUriProtocol(ext)
  if (protocol) {
    const isTemplate = isTemplateFile(ext)
    const command = isTemplate ? 'nft' : 'ofe'
    const query = isTemplate && params && isWindows() ? `${toQueryString(params)}` : ''
    const url = `${protocol}:${command}|u|${uri}${query}`
    console.info('opening document', url)
    window.open(url)
  } else if (ext === 'pdf') {
    console.info('opening pdf document', uri)
    window.open(uri)
  } else if (IMAGE_FILES.includes(ext)) {
    console.info('opening image', uri)
    window.open(uri)
  } else if (EMAIL_FILES.includes(ext)) {
    console.info('opening email', uri)
    window.open(uri)
  } else {
    console.info('could not find a suitable application for', uri)
  }
}

/**
 * Creates a parameter object for use with openDocument()
 */
export function constructDocumentParams (userData, context) {
  return {
    // From userData
    pds_user: userData.name || '',
    pds_email: userData.email || '',
    pds_phone: userData.phone || '',
    pds_city: userData.city || '',
    pds_address: userData.address || '',
    pds_userhomesite: userData.homepage || '',
    pds_contact: userData.collection_name || '',
    pds_title: userData.object_type_name || '',
    pds_zipcode: userData.postal_code || '',
    // From context
    pds_type: (context && context.type) || '',
    pds_id: (context && context.id) || '',
    pds_eid: (context && context.eid) || ''
  }
}

/**
 * Returns true if there is a matching office protocol for the
 * file type on the url.
 */
export function canOpenDocument (uri) {
  if (typeof uri === 'string') {
    const ext = getFileExtension(uri)
    return !!(getOfficeUriProtocol(ext) || ext === 'pdf' || IMAGE_FILES.includes(ext)) || EMAIL_FILES.includes(ext)
  } else {
    return false
  }
}

/**
 * Function for figuring out if an item should be selected in a
 * tree view
 */
export function isSelected (selected) {
  return (item) => {
    if (selected) {
      return item.type === selected.type && item.id === selected.id
    } else {
      return false
    }
  }
}

/**
 * Categories are really document libraries under the parent department
 */
export function getItemUrl (item) {
  return item.type === 'category' ? item.collection_url : item.url
}

/**
 * Category document library uses category.id. All other items use 'files'
 * as their document library root.
 */
export function getItemRoot (item) {
  if (item.type === 'category') {
    return `files_${item.id}`
  } else if (item.type === 'templates') {
    return 'pdsMallar'
  } else {
    return 'files'
  }
}

/**
 * Build a url using origin of scRoot together with
 * ServerRelativeUrl
 */
export function buildFileItemUrl (scRoot, file) {
  const parsed = parseUrl(scRoot)
  if (typeof file.ServerRelativeUrl === 'string') {
    return encodeURI(`${parsed.origin}${file.ServerRelativeUrl}`)
  } else {
    return ''
  }
}

/**
 * Get the item icon based on file extension
 */
export function getItemIcon (item) {
  const ext = getFileExtension(item?.name)
  if (item.type === 'file') {
    if (WORD_FILES.includes(ext)) {
      return <Word />
    } else if (WORD_TEMPLATE_FILES.includes(ext)) {
      return <Word width={20} height={20} />
    } else if (EXCEL_FILES.includes(ext)) {
      return <Excel width={20} height={20} />
    } else if (EXCEL_TEMPLATE_FILES.includes(ext)) {
      return <Excel width={20} height={20} />
    } else if (POWERPOINT_FILES.includes(ext)) {
      return <Powerpoint width={20} height={20} />
    } else if (POWERPOINT_TEMPLATE_FILES.includes(ext)) {
      return <Powerpoint width={20} height={20} />
    } else if (ext === 'pdf') {
      return <PdfFileIcon width={20} height={20} />
    }
    return null
  }
  return <Folder width={24} height={24} />
}

/**
 *
 * Get the site suffix from the url parameters.
 */
export function getSiteSuffix (urlParam) {
  let type = ''
  switch (urlParam.pds_type) {
    case 'contact':
      type = 'c'
      break
    case 'project':
      type = 'p'
      break
    case 'department':
      type = 'i'
      break
    case 'category':
      type = 'i'
      break
      // TODO: Handle this separetely since the suffix will be _i<id>/files_<another-id>
  }

  if (urlParam.pds_eid) {
    // TODO: Fetch client/project based on eid
    const pdsId = urlParam.pds_id
    return `${type}${pdsId}`
  } else {
    return `${type}${urlParam.pds_id}`
  }
}

/**
 * Waits delay milliseconds.
 * Returns a promise.
 */
export function wait (delay) {
  return new Promise(resolve => setTimeout(resolve, delay))
}

/**
 * Return true if this is a Windows machine.
 */
export function isWindows () {
  return navigator.platform.startsWith('Win')
}

/**
 * Return true if this is a Mac machine.
 */
export function isMac () {
  return navigator.platform.startsWith('Mac')
}
